/**
 * The classifier stores the technician hand-off as a SINGLE `summary` string that
 * embeds two labelled sections, e.g.:
 *
 *   Summary: <recap + why it stalled>
 *   Next Steps: <concrete next step(s)>
 *
 * The UI splits it into those two parts for display (bold "Summary" / "Next Steps"
 * headings). Parsing is tolerant of optional markdown (**bold**, ## headings) and
 * casing. If no "Next Steps" label is present, the whole string is treated as the
 * summary and nextSteps is null.
 */
export interface SummarySections {
  summary: string | null;
  nextSteps: string | null;
}

/** Strip a leading "Summary" label (and any markdown/colon around it) from a chunk. */
function stripSummaryLabel(s: string): string {
  return s.trim().replace(/^[ \t>*#_-]*summary[ \t*_]*:[ \t*_]*/i, "").trim();
}

/**
 * Split a stored summary string into its Summary / Next Steps sections.
 *
 * The classifier is told to write "Summary: ... Next Steps: ..." but gpt-4o-mini
 * routinely emits both on the SAME line (no newline before the label), so we split
 * on the "Next Steps:" label wherever it appears — line-start or inline — tolerating
 * optional markdown around it. The colon is required so the label isn't confused
 * with the phrase "next steps" in prose. No label present → the whole string is the
 * summary (old pre-split rows).
 */
export function parseSummarySections(raw: string | null | undefined): SummarySections {
  if (!raw) return { summary: null, nextSteps: null };
  const text = String(raw).trim();
  if (!text) return { summary: null, nextSteps: null };

  // First "Next Steps:" label anywhere in the string (inline or line-start).
  const marker = text.match(/\s*[*_#>-]*\s*next\s*steps[ \t*_]*:[ \t*_]*/i);
  if (!marker || marker.index === undefined) {
    return { summary: stripSummaryLabel(text) || null, nextSteps: null };
  }
  const before = text.slice(0, marker.index);
  const after = text.slice(marker.index + marker[0].length);
  return {
    summary: stripSummaryLabel(before) || null,
    nextSteps: after.trim() || null,
  };
}
